import {
  parseLevelMessage,
  parseServerMessage,
  type ClientMessage,
  type IceCandidate,
  type LevelMessage,
  type StationRole,
} from "../shared/protocol";
import { rmsToDb } from "./noise";
import { getDeviceId } from "./push";
import { reconnectDelay } from "./reconnect";

interface MonitorCallbacks {
  onStatus(message: string, kind?: "normal" | "good" | "warning"): void;
  onLevel(db: number): void;
  onError(message: string): void;
  onRoleUnavailable(role: StationRole): void;
  onAuthenticationLost(): void;
  onPlaybackBlocked(blocked: boolean): void;
  onNoiseDetected(at: number): void;
}

interface PeerState {
  connection: RTCPeerConnection;
  dataChannel: RTCDataChannel | null;
  pendingCandidates: IceCandidate[];
}

const peerConfiguration: RTCConfiguration = {
  iceServers: [{ urls: "stun:stun.cloudflare.com:3478" }],
};

export class MonitorSession {
  private readonly clientId = crypto.randomUUID().replaceAll("-", "");
  private readonly deviceId = getDeviceId();
  private socket: WebSocket | null = null;
  private readonly peers = new Map<string, PeerState>();
  private localStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private remoteAudio: HTMLAudioElement | null = null;
  private reconnectTimer: number | null = null;
  private reconnectAttempt = 0;
  private stopped = false;
  private meterFrame = 0;
  private lastAnalysisAt = 0;
  private lastLevelSentAt = 0;
  private alertThresholdDb = -40;
  private pushEnabled = false;
  private loudSince: number | null = null;
  private lastNoiseSampleAt = 0;
  private readonly visibilityHandler = () => {
    if (this.role === "parent") this.send({ type: "parent-visibility", visible: !document.hidden });
  };

  constructor(
    readonly role: StationRole,
    private readonly callbacks: MonitorCallbacks,
  ) {
    if (role === "parent") {
      this.remoteAudio = document.querySelector<HTMLAudioElement>("#remote-audio");
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
  }

  async start(): Promise<void> {
    if (this.role === "baby") {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("This browser cannot access a microphone. Open the HTTPS site in Safari or Chrome.");
      }
      try {
        this.localStream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          video: false,
        });
      } catch (error) {
        if (error instanceof DOMException && (error.name === "NotAllowedError" || error.name === "SecurityError")) {
          throw new Error("Microphone access was denied. Allow it in browser settings, then try again.");
        }
        throw new Error("The microphone could not be started. Check that another app is not using it, then retry.");
      }
      this.startMeter();
    }
    this.connectSocket();
  }

  async resumeAudio(): Promise<void> {
    if (!this.remoteAudio?.srcObject) {
      this.callbacks.onPlaybackBlocked(true);
      return;
    }
    try {
      this.remoteAudio.muted = false;
      await this.remoteAudio.play();
      this.callbacks.onPlaybackBlocked(this.remoteAudio.paused);
    } catch {
      this.callbacks.onPlaybackBlocked(true);
    }
  }

  updateAlertSettings(thresholdDb: number, pushEnabled: boolean): void {
    this.alertThresholdDb = thresholdDb;
    this.pushEnabled = pushEnabled;
    if (this.role === "parent") {
      this.send({ type: "alert-settings", deviceId: this.deviceId, thresholdDb, pushEnabled });
    }
  }

  stop(): void {
    this.stopped = true;
    if (this.reconnectTimer !== null) window.clearTimeout(this.reconnectTimer);
    this.socket?.close(1000, "Station stopped");
    this.socket = null;
    this.closeAllPeers();
    this.localStream?.getTracks().forEach((track) => track.stop());
    this.localStream = null;
    document.removeEventListener("visibilitychange", this.visibilityHandler);
    if (this.meterFrame) cancelAnimationFrame(this.meterFrame);
    this.remoteAudio?.pause();
    if (this.remoteAudio) this.remoteAudio.srcObject = null;
    this.remoteAudio = null;
    void this.audioContext?.close();
    this.audioContext = null;
  }

  private connectSocket(): void {
    if (this.stopped) return;
    this.callbacks.onStatus(this.reconnectAttempt ? "Reconnecting…" : "Connecting…");
    const protocol = location.protocol === "https:" ? "wss:" : "ws:";
    const socket = new WebSocket(`${protocol}//${location.host}/api/signal`);
    this.socket = socket;

    socket.addEventListener("open", () => {
      if (this.socket !== socket || this.stopped) return;
      this.reconnectAttempt = 0;
      this.send({ type: "join", role: this.role, clientId: this.clientId, deviceId: this.deviceId });
    });
    socket.addEventListener("message", (event) => {
      if (typeof event.data !== "string") return;
      const message = parseServerMessage(event.data);
      if (!message) return;
      void this.handleServerMessage(message).catch(() => {
        this.callbacks.onError("An audio connection failed. Reconnecting may fix it.");
      });
    });
    socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.socket = null;
      this.closeAllPeers();
      if (!this.stopped) void this.scheduleReconnect();
    });
    socket.addEventListener("error", () => socket.close());
  }

  private async scheduleReconnect(): Promise<void> {
    try {
      const response = await fetch("/api/session", { cache: "no-store" });
      const body = (await response.json()) as { authenticated?: boolean };
      if (!body.authenticated) {
        this.stopped = true;
        this.callbacks.onAuthenticationLost();
        return;
      }
    } catch {
      // Continue retrying after network interruptions.
    }
    const delay = reconnectDelay(this.reconnectAttempt++);
    this.callbacks.onStatus(`Connection lost. Retrying in ${Math.ceil(delay / 1000)}s…`, "warning");
    this.reconnectTimer = window.setTimeout(() => this.connectSocket(), delay);
  }

  private async handleServerMessage(message: NonNullable<ReturnType<typeof parseServerMessage>>): Promise<void> {
    switch (message.type) {
      case "joined":
        if (this.role === "parent") {
          this.send({
            type: "alert-settings",
            deviceId: this.deviceId,
            thresholdDb: this.alertThresholdDb,
            pushEnabled: this.pushEnabled,
          });
          this.send({ type: "parent-visibility", visible: !document.hidden });
        }
        return;
      case "waiting":
        this.callbacks.onStatus(`Waiting for the ${this.role === "baby" ? "parent" : "baby"} station…`);
        return;
      case "pair-ready":
        this.callbacks.onStatus("Other station found. Connecting audio…");
        if (this.role === "baby") await this.makeOffer(message.peerId);
        return;
      case "offer": {
        if (this.role !== "parent") return;
        this.closePeer(message.peerId);
        const state = this.ensurePeer(message.peerId);
        await state.connection.setRemoteDescription(message.sdp);
        await this.flushCandidates(state);
        await state.connection.setLocalDescription(await state.connection.createAnswer());
        this.send({
          type: "answer",
          peerId: message.peerId,
          sdp: { type: "answer", sdp: state.connection.localDescription!.sdp },
        });
        return;
      }
      case "answer": {
        if (this.role !== "baby") return;
        const state = this.peers.get(message.peerId);
        if (!state) return;
        await state.connection.setRemoteDescription(message.sdp);
        await this.flushCandidates(state);
        return;
      }
      case "ice-candidate": {
        const state = this.peers.get(message.peerId);
        if (!state) return;
        if (!state.connection.remoteDescription) state.pendingCandidates.push(message.candidate);
        else await state.connection.addIceCandidate(message.candidate);
        return;
      }
      case "alert-settings":
        if (this.role === "baby") {
          this.alertThresholdDb = message.thresholdDb;
          this.loudSince = null;
        }
        return;
      case "noise-detected":
        if (this.role === "parent") this.callbacks.onNoiseDetected(message.at);
        return;
      case "partner-left":
        this.closePeer(message.peerId);
        if (this.role === "parent" || this.peers.size === 0) {
          this.callbacks.onStatus(`The ${this.role === "baby" ? "parent" : "baby"} station disconnected. Waiting…`, "warning");
        } else {
          this.updateBabyStatus();
        }
        return;
      case "role-unavailable":
        this.stopped = true;
        this.socket?.close();
        this.callbacks.onRoleUnavailable(message.role);
        return;
      case "error":
        this.callbacks.onError(message.message);
    }
  }

  private async makeOffer(peerId: string): Promise<void> {
    this.closePeer(peerId);
    const state = this.ensurePeer(peerId);
    await state.connection.setLocalDescription(await state.connection.createOffer());
    this.send({ type: "offer", peerId, sdp: { type: "offer", sdp: state.connection.localDescription!.sdp } });
  }

  private ensurePeer(peerId: string): PeerState {
    const existing = this.peers.get(peerId);
    if (existing) return existing;
    const connection = new RTCPeerConnection(peerConfiguration);
    const state: PeerState = { connection, dataChannel: null, pendingCandidates: [] };
    this.peers.set(peerId, state);

    connection.addEventListener("icecandidate", (event) => {
      if (event.candidate) {
        this.send({ type: "ice-candidate", peerId, candidate: event.candidate.toJSON() });
      }
    });
    connection.addEventListener("connectionstatechange", () => {
      if (this.peers.get(peerId) !== state) return;
      if (connection.connectionState === "connected") {
        if (this.role === "parent") this.callbacks.onStatus("Live audio connected", "good");
        else this.updateBabyStatus();
      }
      if (connection.connectionState === "failed") {
        this.closePeer(peerId);
        this.callbacks.onStatus("An audio connection failed. Waiting to reconnect…", "warning");
      }
    });

    if (this.role === "baby") {
      this.localStream?.getAudioTracks().forEach((track) => connection.addTrack(track, this.localStream!));
      this.attachDataChannel(state, connection.createDataChannel("noise-level", { ordered: false, maxRetransmits: 0 }));
    } else {
      connection.addEventListener("datachannel", (event) => this.attachDataChannel(state, event.channel));
      connection.addEventListener("track", (event) => {
        this.playRemoteStream(event.streams[0] ?? new MediaStream([event.track]));
      });
    }
    return state;
  }

  private attachDataChannel(state: PeerState, channel: RTCDataChannel): void {
    state.dataChannel = channel;
    if (this.role === "parent") {
      channel.addEventListener("message", (event) => {
        if (typeof event.data !== "string") return;
        const level: LevelMessage | null = parseLevelMessage(event.data);
        if (level) this.callbacks.onLevel(level.db);
      });
    }
  }

  private playRemoteStream(stream: MediaStream): void {
    if (!this.remoteAudio) return;
    this.remoteAudio.srcObject = stream;
    void this.resumeAudio();
  }

  private async flushCandidates(state: PeerState): Promise<void> {
    const candidates = state.pendingCandidates.splice(0);
    for (const candidate of candidates) await state.connection.addIceCandidate(candidate);
  }

  private closePeer(peerId: string): void {
    const state = this.peers.get(peerId);
    if (!state) return;
    state.dataChannel?.close();
    state.connection.close();
    this.peers.delete(peerId);
    if (this.role === "parent" && this.peers.size === 0) {
      this.callbacks.onLevel(-100);
      this.remoteAudio?.pause();
      if (this.remoteAudio) this.remoteAudio.srcObject = null;
    }
  }

  private closeAllPeers(): void {
    for (const peerId of [...this.peers.keys()]) this.closePeer(peerId);
  }

  private updateBabyStatus(): void {
    const connected = [...this.peers.values()].filter((state) => state.connection.connectionState === "connected").length;
    if (connected > 0) this.callbacks.onStatus(`Live · ${connected} parent${connected === 1 ? "" : "s"}`, "good");
  }

  private send(message: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }

  private startMeter(): void {
    if (!this.localStream) return;
    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(this.localStream);
    const analyser = this.audioContext.createAnalyser();
    analyser.fftSize = 1_024;
    analyser.smoothingTimeConstant = 0.65;
    source.connect(analyser);
    this.analyser = analyser;
    const samples = new Float32Array(analyser.fftSize);

    const update = (now: number) => {
      if (this.stopped || !this.analyser) return;
      if (now - this.lastAnalysisAt < 100) {
        this.meterFrame = requestAnimationFrame(update);
        return;
      }
      this.lastAnalysisAt = now;
      this.analyser.getFloatTimeDomainData(samples);
      const db = rmsToDb(samples);
      this.callbacks.onLevel(db);
      this.checkForNoiseAlert(db);
      if (now - this.lastLevelSentAt >= 100) {
        const payload = JSON.stringify({ type: "level", db } satisfies LevelMessage);
        for (const state of this.peers.values()) {
          if (state.dataChannel?.readyState === "open") state.dataChannel.send(payload);
        }
        this.lastLevelSentAt = now;
      }
      this.meterFrame = requestAnimationFrame(update);
    };
    this.meterFrame = requestAnimationFrame(update);
  }

  private checkForNoiseAlert(db: number): void {
    const now = Date.now();
    if (db < this.alertThresholdDb) {
      this.loudSince = null;
      return;
    }
    this.loudSince ??= now;
    if (now - this.loudSince >= 500 && now - this.lastNoiseSampleAt >= 1_000) {
      this.send({ type: "noise-alert", db });
      this.lastNoiseSampleAt = now;
    }
  }
}
